import React, { useState, useEffect } from 'react';
import { authService } from '../../../services/authService';
import { groupBookingsService } from '../../../services/groupBookingsService';

// Voyager Pro reskin - VISUAL ONLY. All state, handlers, API calls, and the
// verify -> code -> details step flow are unchanged from the pre-reskin
// version; only the markup/styling was ported from travelieTheme.js inline
// styles to Tailwind + CSS vars to match the rest of the Groups reskin.
// One small correctness fix: the payment-status badge color map now covers
// the 4-state enum (unpaid/deposit_paid/partial/paid_in_full) introduced by
// the Groups DB migration this session, instead of the old/stale
// completed/pending/partial/failed set.

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors";

const PAY_STATUS_STYLE = {
  paid_in_full: 'bg-emerald-50 text-emerald-700',
  deposit_paid: 'bg-amber-50 text-amber-700',
  partial: 'bg-orange-50 text-orange-700',
  unpaid: 'bg-red-50 text-red-600',
  // legacy values kept as a fallback in case any older row/record still has them
  completed: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-red-50 text-red-600',
  failed: 'bg-red-50 text-red-600',
};

function PaymentStatusBadge({ status }) {
  const cls = PAY_STATUS_STYLE[status] || PAY_STATUS_STYLE.unpaid;
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cls}`}>{(status || 'unpaid').replace(/_/g, ' ')}</span>;
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider">{label}</label>
      <div className="text-sm text-[var(--foreground)]">{children}</div>
    </div>
  );
}

function Section({ title, children, tone }) {
  const toneCls = tone === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-white border-[var(--border)]';
  return (
    <div className={`p-5 rounded-2xl border shadow-sm ${toneCls}`}>
      <h3 className="text-sm font-bold text-[var(--foreground)] mb-3">{title}</h3>
      {children}
    </div>
  );
}

const SecureGroupPaymentModal = ({ participant, onClose }) => {
  const [step, setStep] = useState('verify'); // 'verify', 'code', 'details'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [sessionExpired, setSessionExpired] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');

  useEffect(() => {
    let timer;
    if (step === 'details' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setSessionExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  const handleRequestVerification = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authService.requestGroupPaymentAccess(participant.id, verificationMethod);
      console.log('Verification request response:', response);
      setStep('code');
    } catch (error) {
      console.error('Error requesting verification:', error);
      console.error('Error response:', error.response);
      let errorMessage = 'Failed to send verification code';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const details = await authService.getGroupPaymentDetails(participant.id, verificationCode);
      setPaymentDetails(details.data);
      setStep('details');
      setTimeLeft(600); // 10 minutes for viewing
    } catch (error) {
      console.error('Error verifying code:', error);
      console.error('Error response:', error.response);
      let errorMessage = 'Invalid verification code';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (participantId) => {
    if (!window.confirm('Are you sure you want to delete this payment information? This action cannot be undone.')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await groupBookingsService.deleteParticipantPayment(participantId);
      alert('Payment information has been successfully deleted.');
      onClose();
    } catch (error) {
      console.error('Error deleting payment:', error);
      setError(error.response?.data?.error || 'Failed to delete payment information');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getBillingAddress = (billingData) => {
    if (!billingData) return 'N/A';
    try {
      const parsed = typeof billingData === 'string' ? JSON.parse(billingData) : billingData;
      return `${parsed.address || ''}, ${parsed.city || ''}, ${parsed.state || ''} ${parsed.zip || ''}`.trim();
    } catch {
      return 'N/A';
    }
  };

  const formatCardNumber = (cardNumber) => {
    if (!cardNumber) return 'N/A';
    return cardNumber.replace(/(.{4})/g, '$1 ').trim();
  };

  if (sessionExpired) {
    return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Session Expired</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
          <div className="px-6 py-10 text-center">
            <div className="text-5xl mb-4">⏰</div>
            <h3 className="text-base font-bold text-[var(--foreground)] mb-2">Security Session Expired</h3>
            <p className="text-sm text-[var(--muted-foreground)]">For security reasons, your access to payment details has expired.</p>
            <p className="text-sm text-[var(--muted-foreground)]">Please close this window and request access again if needed.</p>
          </div>
          <div className="flex justify-end px-6 py-4 border-t border-[var(--border)]">
            <button onClick={onClose} disabled={loading} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50" style={{ backgroundColor: 'var(--primary)' }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
          <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">
            Secure Payment Details — {participant.first_name} {participant.last_name}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="px-6 py-6 overflow-y-auto">
          {step === 'verify' && (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">🔐</div>
              <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-lg text-[var(--foreground)] mb-1">Security Verification Required</h3>

              <div className="mt-6 mb-6">
                <h4 className="text-xs font-bold text-[var(--muted-foreground)] uppercase tracking-wider mb-3">Choose verification method</h4>
                <div className="flex gap-3 justify-center">
                  <label className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-xl cursor-pointer transition-colors ${verificationMethod === 'email' ? 'border-[var(--accent)] bg-[var(--muted)]/40' : 'border-[var(--border)]'}`}>
                    <input type="radio" name="verificationMethod" value="email" checked={verificationMethod === 'email'} onChange={e => setVerificationMethod(e.target.value)} />
                    <span className="text-sm">📧 Email</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-xl cursor-pointer transition-colors ${verificationMethod === 'sms' ? 'border-[var(--accent)] bg-[var(--muted)]/40' : 'border-[var(--border)]'}`}>
                    <input type="radio" name="verificationMethod" value="sms" checked={verificationMethod === 'sms'} onChange={e => setVerificationMethod(e.target.value)} />
                    <span className="text-sm">📱 SMS</span>
                  </label>
                </div>
              </div>

              <div className="p-4 bg-[var(--muted)]/40 rounded-xl text-left space-y-1">
                <h4 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-wider mb-2">Participant Information</h4>
                <p className="text-sm text-[var(--foreground)]"><strong>Name:</strong> {participant.first_name} {participant.last_name}</p>
                <p className="text-sm text-[var(--foreground)]"><strong>Email:</strong> {participant.email}</p>
                <p className="text-sm text-[var(--foreground)]"><strong>Package:</strong> {participant.package_selected}</p>
                <p className="text-sm text-[var(--foreground)]"><strong>Amount:</strong> ${parseFloat(participant.total_amount || 0).toFixed(2)}</p>
              </div>

              {error && <div className="mt-4 px-3 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm">{error}</div>}

              <div className="flex gap-3 justify-center mt-6">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
                <button
                  onClick={handleRequestVerification}
                  disabled={loading}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  {loading ? 'Sending…' : 'Send Verification Code'}
                </button>
              </div>
            </div>
          )}

          {step === 'code' && (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">📧</div>
              <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-lg text-[var(--foreground)] mb-1">Enter Verification Code</h3>
              <p className="text-sm text-[var(--muted-foreground)]">We've sent a 6-digit verification code to your registered email address.</p>

              <div className="mt-6 flex justify-center">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  className="w-full max-w-[260px] px-4 py-3 text-2xl font-mono text-center border-2 rounded-xl tracking-[0.5em] focus:outline-none"
                  style={{ borderColor: 'var(--primary)' }}
                />
              </div>

              {error && <div className="mt-4 px-3 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm">{error}</div>}

              <div className="flex gap-3 justify-center mt-6">
                <button onClick={() => setStep('verify')} className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Back</button>
                <button
                  onClick={handleVerifyCode}
                  disabled={loading || verificationCode.length !== 6}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  {loading ? 'Verifying…' : 'Verify Code'}
                </button>
              </div>
            </div>
          )}

          {step === 'details' && paymentDetails && (
            <div className="space-y-4">
              <div className="px-3 py-2.5 bg-amber-50 text-amber-800 rounded-xl text-center text-sm">
                ⏱️ Session expires in: <strong>{formatTime(timeLeft)}</strong>
              </div>

              <Section title="Participant Information">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Full Name">{participant.first_name} {participant.last_name}</Field>
                  <Field label="Email">{participant.email}</Field>
                  <Field label="Phone">{participant.phone}</Field>
                  <Field label="Package">{participant.package_selected}</Field>
                </div>
              </Section>

              <Section title="Payment Information">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Total Amount">${parseFloat(participant.total_amount || 0).toFixed(2)}</Field>
                  <Field label="Payment Status"><PaymentStatusBadge status={participant.payment_status} /></Field>
                  <Field label="Registration Date">{formatDate(participant.joined_at)}</Field>
                </div>
              </Section>

              {paymentDetails.card_details && (
                <Section title="🔒 Credit Card Details" tone="warning">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Card Number">
                      <span className="text-lg font-mono tracking-widest text-amber-800 font-semibold">{formatCardNumber(paymentDetails.card_details.number)}</span>
                    </Field>
                    <Field label="Expiry Date">
                      <span className="inline-block px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg">{paymentDetails.card_details.exp_month}/{paymentDetails.card_details.exp_year}</span>
                    </Field>
                    <Field label="CVV">
                      <span className="inline-block px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg">{paymentDetails.card_details.cvv}</span>
                    </Field>
                    <Field label="Cardholder Name">
                      <span className="inline-block px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg">{paymentDetails.card_details.name}</span>
                    </Field>
                  </div>
                  <button
                    onClick={() => handleDeletePayment(participant.id)}
                    title="Delete payment information"
                    className="mt-4 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:brightness-105 transition-all"
                  >
                    🗑️ Delete Payment Data
                  </button>
                </Section>
              )}

              {paymentDetails.billing_address && (
                <Section title="Billing Address">
                  <Field label="Address">{getBillingAddress(paymentDetails.billing_address)}</Field>
                </Section>
              )}

              {paymentDetails.payment_sessions && paymentDetails.payment_sessions.length > 0 && (
                <Section title="💳 Payment Sessions">
                  <div className="space-y-2">
                    {paymentDetails.payment_sessions.map((session, index) => (
                      <div key={index} className="p-3 bg-[var(--muted)]/40 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[var(--muted-foreground)]">Session ID: {session.session_id}</span>
                          <span className="text-sm font-semibold text-emerald-700">${parseFloat(session.amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Status"><PaymentStatusBadge status={session.status} /></Field>
                          <Field label="Created">{formatDate(session.created_at)}</Field>
                          {session.payment_method && <Field label="Payment Method">{session.payment_method}</Field>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {paymentDetails.payments && paymentDetails.payments.length > 0 && (
                <Section title="✅ Completed Payments">
                  <div className="space-y-2">
                    {paymentDetails.payments.map((payment, index) => (
                      <div key={index} className="p-3 bg-[var(--muted)]/40 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[var(--muted-foreground)]">Transaction ID: {payment.transaction_id}</span>
                          <span className="text-sm font-semibold text-emerald-700">${parseFloat(payment.amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Status"><PaymentStatusBadge status={payment.status || 'completed'} /></Field>
                          <Field label="Payment Date">{formatDate(payment.created_at)}</Field>
                          {payment.payment_method && <Field label="Payment Method">{payment.payment_method}</Field>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {participant.booking_details && (() => {
                const bookingDetails = typeof participant.booking_details === 'string'
                  ? JSON.parse(participant.booking_details) : participant.booking_details;
                return (
                  <Section title="📋 Booking Details">
                    <div className="grid grid-cols-2 gap-4">
                      {bookingDetails.address && <div className="col-span-2"><Field label="Participant Address">{bookingDetails.address}</Field></div>}
                      {bookingDetails.special_requests && <div className="col-span-2"><Field label="Special Requests">{bookingDetails.special_requests}</Field></div>}
                      {bookingDetails.additional_guests && bookingDetails.additional_guests.length > 0 && (
                        <div className="col-span-2">
                          <Field label={`Additional Guests (${bookingDetails.additional_guests.length})`}>
                            <div className="space-y-1.5 mt-1">
                              {bookingDetails.additional_guests.map((guest, idx) => (
                                <div key={idx} className="px-3 py-2 bg-[var(--muted)]/60 rounded-lg text-sm">
                                  <strong>{guest.name}</strong>{guest.age && ` (Age: ${guest.age})`}
                                </div>
                              ))}
                            </div>
                          </Field>
                        </div>
                      )}
                    </div>
                  </Section>
                );
              })()}
            </div>
          )}
        </div>

        {step === 'details' && paymentDetails && (
          <div className="flex justify-end px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
            <button onClick={onClose} disabled={loading} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50" style={{ backgroundColor: 'var(--primary)' }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecureGroupPaymentModal;
